"use client";

import { useMemo, useState } from "react";
import { Link } from "@/i18n/navigation";
import Button from "@/components/ui/Button/Button";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    Avancer,
    Curseur,
    Ecran,
    Liste,
    Question,
    Recommencer,
    Resultat,
    euros,
    eurosPrecis,
    remplir,
    useParcours,
} from "./Journey";
import { IJ_SOCIETE, arretEurl, arretSasu } from "@/lib/tools/companySickLeave";
import { expliquerArretSociete } from "@/lib/tools/explanations";
import {
    IndiceExplication,
    MontantExplicable,
    PanneauExplication,
    phraseAvecMontants as phrase,
    useExplications,
} from "./Explanation";
import { useDossierOutil, useSignalerDossier } from "./ToolsDossier";
import { reprendre } from "@/lib/tools/dossier";

/**
 * Arrêt maladie : ce que toucherait un dirigeant de société.
 *
 * LA RÉPONSE TIENT SOUVENT EN UN MOT : RIEN. Un président de SASU payé tout en
 * dividendes, ou au seul salaire des quatre trimestres, n'ouvre aucun droit ;
 * un gérant d'EURL affilié depuis moins d'un an non plus. L'écran le dit en
 * premier, avec le montant qu'il faudrait pour que ça change, plutôt que
 * d'afficher un zéro sans explication.
 *
 * Le calcul et ses hypothèses sont dans lib/tools/companySickLeave.js ;
 * npm run check:company-sick-leave les vérifie contre les exemples officiels.
 */

const PHASES = {
    forme: 0,
    micro: 0,
    metier: 0,
    avocat: 0,
    affiliation: 0,
    revenu: 1,
    salaire: 1,
    duree: 2,
    resultat: 3,
};

const L = {
    fr: {
        phases: ["Statut", "Revenu", "Arrêt"],
        retour: "Retour",
        forme: {
            titre: "Quelle est votre situation ?",
            aide: "Un président de SASU et un gérant d'EURL ne relèvent pas du même régime : leurs indemnités ne se calculent pas pareil.",
            choix: [
                { cle: "sasu", icone: "user-check-linear", titre: "Président de SASU", detail: "Vous êtes assimilé salarié." },
                { cle: "eurl", icone: "buildings-2-linear", titre: "Gérant d'EURL", detail: "Vous êtes travailleur indépendant." },
                { cle: "micro", icone: "case-minimalistic-linear", titre: "Micro-entrepreneur", detail: "Vous déclarez votre chiffre d'affaires à l'Urssaf." },
            ],
        },
        micro: {
            titre: "En micro, votre indemnité part de votre chiffre d'affaires",
            aide: "C'est votre chiffre d'affaires après abattement qui compte, et un autre simulateur fait exactement ce calcul.",
            points: ["Vous y verrez aussi le chiffre d'affaires à partir duquel vous ouvrez un droit, selon votre activité."],
            lien: "Simulateur d'indemnités journalières",
        },
        metier: {
            titre: "Exercez-vous une profession libérale réglementée ?",
            aide: "Les professions réglementées ont un plafond plus haut, mais une durée d'indemnisation plus courte.",
            choix: [
                { cle: "non", icone: "shop-linear", titre: "Non", detail: "Commerce, artisanat, conseil, développement, formation, design." },
                { cle: "oui", icone: "stethoscope-linear", titre: "Oui", detail: "Architecte, psychologue, ostéopathe, expert-comptable : vous cotisez à la Cipav ou à une autre caisse libérale." },
                { cle: "avocat", icone: "scale-linear", titre: "Je suis avocat", detail: "Vous cotisez à la caisse des avocats." },
            ],
        },
        avocat: {
            titre: "Les avocats ont un régime à part",
            aide: "Les indemnités de l'assurance maladie des indépendants ne concernent pas les avocats : leur couverture passe par leur ordre et leur caisse. Nous ne la calculons pas ici.",
            points: ["Votre barreau vous dira quelle prévoyance vous couvre, et à partir de quel jour."],
        },
        affiliation: {
            titre: "Êtes-vous affilié comme indépendant depuis au moins un an ?",
            aide: "Il faut douze mois d'affiliation continue avant d'avoir droit aux indemnités. Les mois passés en micro ou en entreprise individuelle comptent, s'il n'y a pas eu de coupure.",
            choix: [
                { cle: "oui", icone: "calendar-mark-linear", titre: "Oui, depuis plus d'un an", detail: "" },
                { cle: "non", icone: "hourglass-line-linear", titre: "Non, depuis moins d'un an", detail: "" },
            ],
        },
        revenu: {
            titre: "Combien vous êtes-vous versé par an, en moyenne sur les trois dernières années ?",
            aide: "Votre rémunération de gérant, nette de cotisations. Si vous avez moins de trois ans d'activité, donnez votre revenu d'une année pleine.",
            remuneration: "Rémunération nette par an",
            dividendes: "Dividendes au-delà de 10 % de votre capital, par an",
            dividendesAide: "Ils comptent aussi dans votre revenu. Laissez zéro si vous n'en prenez pas.",
            suivant: "Continuer",
        },
        salaire: {
            titre: "Quel salaire brut vous versez-vous chaque mois ?",
            aide: "Le brut de votre fiche de paie, avant les cotisations. Vos dividendes ne comptent pas.",
            libelle: "Salaire brut mensuel",
            aucun: "Aucun salaire : vous ne vous versez que des dividendes.",
            suivant: "Continuer",
        },
        duree: {
            titre: "Combien de temps durerait votre arrêt ?",
            aide: "En jours calendaires, week-ends compris.",
            libelle: "Durée de l'arrêt",
            jours: "{n} jours",
            suivant: "Voir ce que je toucherais",
        },
        resultat: {
            titre: "Votre arrêt maladie",
            parJour: "Vous toucheriez {montant} par jour",
            total: "Soit {total} bruts pour {jours} jours d'arrêt, {net} après CSG et CRDS. Les trois premiers jours ne sont jamais payés.",
            rien: "Vous ne toucheriez rien",
            pourquoi: {
                sasu: "Vous devez avoir cotisé sur au moins {seuil} de salaire brut sur les six mois qui précèdent l'arrêt, soit environ {mensuel} par mois. Avec {salaire} par mois, vous n'y êtes pas.",
                sasuDividendes: "Vos dividendes ne comptent pas : sans salaire, vous ne cotisez pas pour les indemnités journalières. Il faudrait vous verser au moins {mensuel} bruts par mois.",
                affiliation: "Il faut douze mois d'affiliation continue comme indépendant. Passé ce délai, votre revenu vous donnerait {montant} par jour.",
                seuil: "En dessous de {minimum} de revenu annuel moyen, un indépendant n'a droit à rien.",
            },
            plafonne: {
                sasu: "Vous êtes au maximum : au-delà de {plafond} bruts par mois, votre indemnité ne monte plus.",
                eurl: "Vous êtes au maximum : au-delà de {plafond} de revenu annuel moyen, votre indemnité ne monte plus.",
            },
            reperesTitre: "Les repères à connaître",
            reperes: {
                sasu: [
                    "Moins de {mensuel} bruts par mois : aucun droit.",
                    "Au-delà de {plafond} bruts par mois : l'indemnité plafonne à {maximum} par jour.",
                    "Le salaire qui valide vos quatre trimestres de retraite ne suffit pas à ouvrir le droit.",
                ],
                eurl: [
                    "Moins de {minimum} de revenu annuel moyen : aucun droit.",
                    "Au-delà de {plafond} : l'indemnité plafonne à {maximum} par jour.",
                    "Douze mois d'affiliation continue sont nécessaires.",
                ],
                reglemente: "Au-delà du 90e jour, l'assurance maladie ne vous indemnise plus : 87 jours au plus par arrêt.",
            },
            tronque: "Votre arrêt dépasse 90 jours : l'assurance maladie ne paie que 87 jours. Au-delà, c'est votre caisse de retraite qui prend le relais quand elle le prévoit : vérifiez auprès d'elle, et regardez votre contrat de prévoyance.",
            prevoyance: {
                titre: "Ce qui complète ces indemnités",
                texte: "Ces indemnités ne remplacent qu'une petite partie de votre revenu, et votre société continue d'avoir des charges. Un contrat de prévoyance comble la différence : regardez ce que prévoit le vôtre avant d'en avoir besoin.",
            },
            impot: "Ces indemnités sont imposables comme un revenu, sauf affection de longue durée exonérante.",
            lienPartage: "Rémunération ou dividendes : trouver le bon partage",
            hypotheses: {
                titre: "Pour ce calcul, nous avons supposé que :",
                sasu: [
                    "vous vous versez le même salaire chaque mois ; payé par trimestre, la caisse pourrait ne retenir aucune paie ;",
                    "seule la condition de salaire est testée : nous ne savons pas comment la caisse compte les heures d'un président sans contrat de travail ;",
                    "l'arrêt commence ce mois-ci et dure six mois au plus.",
                ],
                eurl: [
                    "votre revenu a été à peu près le même ces trois dernières années ;",
                    "depuis 2025, votre revenu est retenu après un abattement de 26 % sur votre rémunération cotisations comprises : selon la façon dont la caisse le reprendra, votre indemnité peut être jusqu'à 8 % plus haute ;",
                    "vos cotisations sont à jour : des cotisations impayées réduisent l'indemnité.",
                ],
            },
            recommencer: "Refaire le calcul",
        },
    },
    en: {
        phases: ["Status", "Income", "Leave"],
        retour: "Back",
        forme: {
            titre: "What is your situation?",
            aide: "A SASU president and an EURL manager are not under the same scheme: their benefits are not calculated the same way.",
            choix: [
                { cle: "sasu", icone: "user-check-linear", titre: "SASU president", detail: "You are treated as an employee." },
                { cle: "eurl", icone: "buildings-2-linear", titre: "EURL manager", detail: "You are self-employed." },
                { cle: "micro", icone: "case-minimalistic-linear", titre: "Micro-entrepreneur", detail: "You declare your turnover to the Urssaf." },
            ],
        },
        micro: {
            titre: "Under the micro, your benefit starts from your turnover",
            aide: "It is your turnover after the allowance that counts, and another calculator does exactly that.",
            points: ["You will also see the turnover you need to qualify, depending on your activity."],
            lien: "Sick leave benefit calculator",
        },
        metier: {
            titre: "Do you work in a regulated liberal profession?",
            aide: "Regulated professions have a higher ceiling, but a shorter period of benefit.",
            choix: [
                { cle: "non", icone: "shop-linear", titre: "No", detail: "Trade, crafts, consulting, development, training, design." },
                { cle: "oui", icone: "stethoscope-linear", titre: "Yes", detail: "Architect, psychologist, osteopath, chartered accountant: you contribute to the Cipav or another liberal fund." },
                { cle: "avocat", icone: "scale-linear", titre: "I am a lawyer", detail: "You contribute to the lawyers' fund." },
            ],
        },
        avocat: {
            titre: "Lawyers have their own scheme",
            aide: "The self-employed sick pay from health insurance does not cover lawyers: their cover goes through their bar and their fund. We do not calculate it here.",
            points: ["Your bar will tell you which provident cover protects you, and from which day."],
        },
        affiliation: {
            titre: "Have you been registered as self-employed for at least a year?",
            aide: "You need twelve months of continuous registration before being entitled to benefits. Months spent as a micro-entrepreneur or sole trader count, if there was no break.",
            choix: [
                { cle: "oui", icone: "calendar-mark-linear", titre: "Yes, for more than a year", detail: "" },
                { cle: "non", icone: "hourglass-line-linear", titre: "No, for less than a year", detail: "" },
            ],
        },
        revenu: {
            titre: "How much have you paid yourself a year, on average over the last three years?",
            aide: "Your pay as a manager, net of contributions. If you have been trading for less than three years, give your income for a full year.",
            remuneration: "Net pay a year",
            dividendes: "Dividends above 10% of your capital, a year",
            dividendesAide: "They count in your income too. Leave zero if you take none.",
            suivant: "Continue",
        },
        salaire: {
            titre: "What gross salary do you pay yourself each month?",
            aide: "The gross on your payslip, before contributions. Your dividends do not count.",
            libelle: "Monthly gross salary",
            aucun: "No salary: you only take dividends.",
            suivant: "Continue",
        },
        duree: {
            titre: "How long would your sick leave last?",
            aide: "In calendar days, weekends included.",
            libelle: "Length of leave",
            jours: "{n} days",
            suivant: "See what I would receive",
        },
        resultat: {
            titre: "Your sick leave",
            parJour: "You would receive {montant} a day",
            total: "That is {total} gross for {jours} days of leave, {net} after CSG and CRDS. The first three days are never paid.",
            rien: "You would receive nothing",
            pourquoi: {
                sasu: "You need to have paid contributions on at least {seuil} of gross salary over the six months before the leave, about {mensuel} a month. With {salaire} a month, you fall short.",
                sasuDividendes: "Your dividends do not count: without a salary, you pay no contributions towards sick pay. You would need to pay yourself at least {mensuel} gross a month.",
                affiliation: "You need twelve months of continuous self-employed registration. After that, your income would give you {montant} a day.",
                seuil: "Below {minimum} of average annual income, a self-employed person is entitled to nothing.",
            },
            plafonne: {
                sasu: "You are at the maximum: above {plafond} gross a month, your benefit no longer rises.",
                eurl: "You are at the maximum: above {plafond} of average annual income, your benefit no longer rises.",
            },
            reperesTitre: "The thresholds to know",
            reperes: {
                sasu: [
                    "Under {mensuel} gross a month: no entitlement.",
                    "Above {plafond} gross a month: the benefit caps at {maximum} a day.",
                    "Paying yourself just enough to validate your four pension quarters does not qualify you.",
                ],
                eurl: [
                    "Under {minimum} of average annual income: no entitlement.",
                    "Above {plafond}: the benefit caps at {maximum} a day.",
                    "Twelve months of continuous registration are required.",
                ],
                reglemente: "After the 90th day, health insurance stops paying: 87 days at most per leave.",
            },
            tronque: "Your leave goes beyond 90 days: health insurance only pays 87 days. After that, your pension fund may take over if its rules provide for it: check with them, and look at your provident contract.",
            prevoyance: {
                titre: "What tops up these benefits",
                texte: "These benefits only replace a small part of your income, and your company still has costs. A provident contract makes up the difference: check what yours covers before you need it.",
            },
            impot: "These benefits are taxable as income, except for an exempt long-term illness.",
            lienPartage: "Salary or dividends: find the right split",
            hypotheses: {
                titre: "For this calculation, we assumed that:",
                sasu: [
                    "you pay yourself the same salary every month; paid quarterly, the fund might count no payslip at all;",
                    "only the salary condition is tested: we do not know how the fund counts the hours of a president without an employment contract;",
                    "the leave starts this month and lasts six months at most.",
                ],
                eurl: [
                    "your income has been roughly the same over the last three years;",
                    "since 2025, your income is taken after a 26% allowance on your pay including contributions: depending on how the fund uses it, your benefit may be up to 8% higher;",
                    "your contributions are up to date: unpaid contributions reduce the benefit.",
                ],
            },
            recommencer: "Run the calculation again",
        },
    },
};

export default function ArretMaladieSociete({ lang = "fr", aujourdhui }) {
    const t = L[lang] || L.fr;
    const r = t.resultat;

    // Le dossier du lecteur, s'il en a un : les réponses qu'il connaît
    // partent déjà posées, et le parcours s'ouvre sur la première qui manque.
    // La micro n'est jamais une valeur de l'outil : c'est un écran de renvoi,
    // où la reprise mène directement.
    const { valeurs: dossier } = useDossierOutil();
    const formeDossier = dossier.forme === "eurl" || dossier.forme === "sasu" ? dossier.forme : undefined;
    const [forme, setForme] = useState(formeDossier ?? null);
    const [reglemente, setReglemente] = useState(dossier.reglemente ?? false);
    const [affilie, setAffilie] = useState(true);
    const [remuneration, setRemuneration] = useState(30000);
    const [dividendes, setDividendes] = useState(0);
    const [salaire, setSalaire] = useState(2500);
    const [jours, setJours] = useState(30);

    const { etape, communs, rang, aller, retour, recommencer } = useParcours(
        "forme",
        () => {
            setForme(null);
            setReglemente(false);
            setAffilie(true);
            setRemuneration(30000);
            setDividendes(0);
            setSalaire(2500);
            setJours(30);
        },
        PHASES,
        reprendre(
            dossier.forme === "micro"
                ? [{ etape: "forme", connu: true }, { etape: "micro" }]
                : [
                      { etape: "forme", connu: formeDossier !== undefined },
                      // Le salaire du président, l'affiliation depuis un an et le
                      // revenu du gérant ne se déduisent pas du dossier : le
                      // parcours s'y arrête.
                      ...(formeDossier === "sasu"
                          ? [{ etape: "salaire", connu: false }]
                          : [
                                { etape: "metier", connu: dossier.reglemente !== undefined },
                                { etape: "affiliation", connu: false },
                            ]),
                      { etape: "resultat" },
                  ],
        ),
    );

    useSignalerDossier({ forme }, etape === "resultat");

    // La date vient de la page : lue des deux côtés, un new Date() ici
    // produirait un écart d'hydratation.
    const debut = useMemo(() => {
        const d = aujourdhui ? new Date(aujourdhui) : new Date();
        return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 12));
    }, [aujourdhui]);

    const calcul = useMemo(() => {
        if (etape !== "resultat") return null;
        if (forme === "sasu") return arretSasu({ salaireMensuel: salaire, debut, jours });
        return arretEurl({ revenuAnnuel: remuneration + dividendes, reglemente, affilie, jours });
    }, [etape, forme, salaire, debut, jours, remuneration, dividendes, reglemente, affilie]);

    // Ce que le revenu donnerait une fois l'affiliation acquise : le lecteur
    // qui se lance veut savoir ce qui l'attend.
    const apresUnAn = useMemo(
        () =>
            etape === "resultat" && forme === "eurl" && !affilie
                ? arretEurl({ revenuAnnuel: remuneration + dividendes, reglemente, affilie: true, jours })
                : null,
        [etape, forme, affilie, remuneration, dividendes, reglemente, jours],
    );

    const communsEcran = { ...communs, phases: t.phases, retour: t.retour, onRetour: retour };
    const sommes = (v) => euros(Math.round(v), lang);
    const centimes = (v) => eurosPrecis(v, lang);
    // Un seul calcul déplié à la fois, sous la phrase qui porte le montant.
    const { ouverte, basculer } = useExplications();
    const lienOutil = (fr, en, libelle, type = "primary") => (
        <Link href={{ pathname: "/tools/[slug]", params: { slug: lang === "fr" ? fr : en } }}>
            <Button type={type} className="press-safe w-full">
                {libelle} →
            </Button>
        </Link>
    );

    if (etape === "forme") {
        return (
            <Question
                key="forme"
                {...communsEcran}
                phase={PHASES.forme}
                rang={rang}
                titre={t.forme.titre}
                aide={t.forme.aide}
                choix={t.forme.choix.map((c) => ({
                    icone: c.icone,
                    titre: c.titre,
                    detail: c.detail,
                    onClick: () => {
                        if (c.cle === "micro") {
                            aller("micro");
                            return;
                        }
                        setForme(c.cle);
                        aller(c.cle === "sasu" ? "salaire" : "metier");
                    },
                }))}
            />
        );
    }

    if (etape === "micro" || etape === "avocat") {
        const texte = t[etape];
        return (
            <Ecran key={etape} {...communsEcran} phase={PHASES[etape]} rang={rang} titre={texte.titre} aide={texte.aide}>
                <Liste items={texte.points} />
                {etape === "micro" && (
                    <div className="mt-5">
                        {lienOutil("simulateur-indemnites-journalieres", "sick-leave-benefit-calculator", t.micro.lien)}
                    </div>
                )}
                <Recommencer onClick={recommencer} libelle={r.recommencer} />
            </Ecran>
        );
    }

    if (etape === "metier" || etape === "affiliation") {
        const texte = t[etape];
        return (
            <Question
                key={etape}
                {...communsEcran}
                phase={PHASES[etape]}
                rang={rang}
                titre={texte.titre}
                aide={texte.aide}
                choix={texte.choix.map((c) => ({
                    icone: c.icone,
                    titre: c.titre,
                    detail: c.detail || undefined,
                    onClick: () => {
                        if (etape === "metier") {
                            if (c.cle === "avocat") {
                                aller("avocat");
                                return;
                            }
                            setReglemente(c.cle === "oui");
                            aller("affiliation");
                        } else {
                            setAffilie(c.cle === "oui");
                            aller("revenu");
                        }
                    },
                }))}
            />
        );
    }

    if (etape === "revenu") {
        return (
            <Ecran key="revenu" {...communsEcran} phase={PHASES.revenu} rang={rang} titre={t.revenu.titre} aide={t.revenu.aide}>
                <div className="space-y-5">
                    <Curseur
                        libelle={t.revenu.remuneration}
                        valeur={remuneration}
                        poser={setRemuneration}
                        minimum={0}
                        maximum={150000}
                        saisie
                        pas={1000}
                        formater={sommes}
                    />
                    <Curseur
                        libelle={t.revenu.dividendes}
                        aide={t.revenu.dividendesAide}
                        valeur={dividendes}
                        poser={setDividendes}
                        minimum={0}
                        maximum={100000}
                        saisie
                        pas={1000}
                        formater={sommes}
                    />
                </div>
                <Avancer onClick={() => aller("duree")}>{t.revenu.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "salaire") {
        return (
            <Ecran key="salaire" {...communsEcran} phase={PHASES.salaire} rang={rang} titre={t.salaire.titre} aide={t.salaire.aide}>
                <Curseur
                    libelle={t.salaire.libelle}
                    aide={salaire === 0 ? t.salaire.aucun : undefined}
                    valeur={salaire}
                    poser={setSalaire}
                    minimum={0}
                    maximum={8000}
                    saisie
                    pas={50}
                    formater={sommes}
                />
                <Avancer onClick={() => aller("duree")}>{t.salaire.suivant}</Avancer>
            </Ecran>
        );
    }

    if (etape === "duree") {
        return (
            <Ecran key="duree" {...communsEcran} phase={PHASES.duree} rang={rang} titre={t.duree.titre} aide={t.duree.aide}>
                <Curseur
                    libelle={t.duree.libelle}
                    valeur={jours}
                    poser={setJours}
                    minimum={4}
                    maximum={180}
                    pas={1}
                    formater={(v) => remplir(t.duree.jours, { n: v })}
                />
                <Avancer onClick={() => aller("resultat")}>{t.duree.suivant}</Avancer>
            </Ecran>
        );
    }

    const c = calcul;
    const sasu = forme === "sasu";
    // Chaque montant dit d'où il vient : ses étapes et ses sources.
    const ex = expliquerArretSociete(c, { salaireMensuel: salaire, revenuAnnuel: remuneration + dividendes, jours }, lang);
    const reperes = sasu
        ? r.reperes.sasu.map((ligne) =>
              remplir(ligne, {
                  mensuel: sommes(c.salaireMensuelOuverture),
                  plafond: sommes(c.plafondMensuel),
                  maximum: centimes(c.maximum),
              }),
          )
        : [
              ...r.reperes.eurl.map((ligne) =>
                  remplir(ligne, {
                      minimum: sommes(IJ_SOCIETE.independant.revenuMinimum),
                      plafond: sommes(c.plafond),
                      maximum: centimes(Math.round(c.maximum * 100) / 100),
                  }),
              ),
              ...(reglemente ? [r.reperes.reglemente] : []),
          ];

    let pourquoi = null;
    if (!c.ouvre) {
        if (sasu) {
            pourquoi =
                salaire === 0
                    ? remplir(r.pourquoi.sasuDividendes, { mensuel: sommes(c.salaireMensuelOuverture) })
                    : remplir(r.pourquoi.sasu, {
                          seuil: sommes(c.seuilOuverture),
                          mensuel: sommes(c.salaireMensuelOuverture),
                          salaire: sommes(salaire),
                      });
        } else if (!c.affilie && apresUnAn?.ouvre) {
            pourquoi = remplir(r.pourquoi.affiliation, { montant: centimes(apresUnAn.parJour) });
        } else {
            pourquoi = remplir(r.pourquoi.seuil, { minimum: sommes(IJ_SOCIETE.independant.revenuMinimum) });
        }
    }

    return (
        <Resultat onRetour={retour} retour={t.retour} key="resultat" titre={r.titre} phases={t.phases} {...communs}>
            {c.ouvre ? (
                <>
                    <div className="mt-2 font-bold text-2xl leading-tight dark:text-white">
                        {phrase(r.parJour, {
                            montant: (
                                <MontantExplicable explication={ex.parJour} ouvert={ouverte === "parJour"} onBasculer={() => basculer("parJour")}>
                                    {centimes(c.parJour)}
                                </MontantExplicable>
                            ),
                        })}
                    </div>
                    {ouverte === "parJour" && <PanneauExplication explication={ex.parJour} lang={lang} />}
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                        {phrase(r.total, {
                            total: (
                                <MontantExplicable explication={ex.total} ouvert={ouverte === "total"} onBasculer={() => basculer("total")}>
                                    {sommes(c.total)}
                                </MontantExplicable>
                            ),
                            jours,
                            net: (
                                <MontantExplicable explication={ex.net} ouvert={ouverte === "net"} onBasculer={() => basculer("net")}>
                                    {sommes(c.net)}
                                </MontantExplicable>
                            ),
                        })}
                    </p>
                    {ouverte === "total" && <PanneauExplication explication={ex.total} lang={lang} />}
                    {ouverte === "net" && <PanneauExplication explication={ex.net} lang={lang} />}
                    <IndiceExplication lang={lang} />
                    {c.plafonne && (
                        <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                            {sasu
                                ? remplir(r.plafonne.sasu, { plafond: sommes(c.plafondMensuel) })
                                : remplir(r.plafonne.eurl, { plafond: sommes(c.plafond) })}
                        </p>
                    )}
                    {c.tronque && <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">{r.tronque}</p>}
                </>
            ) : (
                <>
                    <div className="mt-2 font-bold text-2xl leading-tight dark:text-white">{r.rien}</div>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">{pourquoi}</p>
                </>
            )}

            <div className="mt-6 rounded-xl border border-gray-200/70 dark:border-white/10 px-4 py-4">
                <div className="font-semibold dark:text-white">{r.reperesTitre}</div>
                <Liste items={reperes} />
            </div>

            <div className="mt-6 rounded-xl border border-primary/30 bg-primary/[0.06] px-4 py-4">
                <div className="flex items-center gap-2 font-semibold dark:text-white">
                    <SolarIcon name="shield-linear" className="w-5 h-5 text-primary" />
                    {r.prevoyance.titre}
                </div>
                <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">{r.prevoyance.texte}</p>
            </div>

            <p className="text-sm text-gray-600 dark:text-lightBlue mt-5 mb-0">{r.impot}</p>

            {sasu && (
                <div className="mt-6">
                    {lienOutil("remuneration-ou-dividendes", "salary-or-dividends", r.lienPartage, "secondary")}
                </div>
            )}

            <div className="mt-6">
                <div className="font-semibold dark:text-white">{r.hypotheses.titre}</div>
                <Liste items={sasu ? r.hypotheses.sasu : r.hypotheses.eurl} />
            </div>

            <Recommencer onClick={recommencer} libelle={r.recommencer} />
        </Resultat>
    );
}
